import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkle, ArrowsClockwise, Newspaper, Lightbulb, Warning,
  ArrowUpRight, MapPin, Buildings, Compass,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/context/I18nContext";

// ---------------------------------------------------------------------------
// AI Insights = Strategic Briefing.
//
// New responsibility (Feb-2026): NOT a news feed. Fetches the latest
// strategic briefing generated by the daily autonomous cycle — public
// industry news → LLM → 2-3 concrete action recommendations tied to the
// user's business profile.
// ---------------------------------------------------------------------------
export default function AIAnalysis() {
  const { locale } = useI18n();
  const t = (tr, en) => (locale === "en" ? en : tr);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get(`/ai-insights${refresh ? "?refresh=true" : ""}`);
      setData(data);
      if (refresh) toast.success(locale === "en" ? "Insights refreshed" : "Analiz güncellendi");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || (locale === "en" ? "Failed to load insights" : "Analiz alınamadı"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [locale]);

  useEffect(() => { load(false); }, [load]);

  return (
    <div className="p-6 md:p-10 space-y-8 max-w-[1200px]">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="acas-eyebrow mb-2 flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" className="text-primary" />
            {t("Sektörel Zeka", "Industry Intelligence")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="ai-insights-title">
            {t("AI Stratejik Brifingi", "AI Strategic Briefing")}
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            {t(
              "Sektörünüzdeki güncel gelişmeleri okuduk, işletme profilinize göre yorumladık ve bu hafta atmanız gereken somut adımları çıkardık. Bir haber özeti değil — size özel stratejik bir not.",
              "We read the latest developments in your industry, interpreted them through your business profile, and distilled the concrete steps you should take this week. Not a news digest — a strategic note for you."
            )}
          </p>
        </div>
        <Button
          onClick={() => load(true)}
          disabled={refreshing || loading}
          data-testid="ai-insights-refresh"
          className="gap-2 self-start"
        >
          <ArrowsClockwise size={14} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? t("Analiz ediliyor…", "Analyzing…") : t("Yeniden analiz et", "Regenerate")}
        </Button>
      </header>

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Skeleton className="h-56 lg:col-span-2" />
          <Skeleton className="h-56" />
        </div>
      )}

      {/* Empty state — no briefing yet */}
      {!loading && data && (!data.summary && (data.recommendations || []).length === 0) && (
        <div className="rounded-lg border border-dashed border-border/60 p-8 text-center" data-testid="ai-insights-empty">
          <div className="w-11 h-11 rounded-full bg-muted/40 grid place-items-center mx-auto mb-3 text-muted-foreground">
            <Newspaper size={18} weight="duotone" />
          </div>
          <div className="font-semibold text-sm mb-2">
            {t("Henüz stratejik brifing üretilmedi.", "No strategic briefing yet.")}
          </div>
          <p className="text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
            {t(
              "Otonom AI çalışanınız günde bir kez sizin için sektörel haberleri toplayıp yorumlar. Kaydınızı ve İşletme Profilinizi tamamlayın; ilk brifing bir sonraki döngüde hazır olacak. Ya da 'Yeniden analiz et' butonu ile şimdi manuel oluşturun.",
              "Your autonomous AI employee collects and interprets industry news once a day. Complete your Business Profile; the first briefing will be ready in the next cycle. Or click 'Regenerate' to build one manually."
            )}
          </p>
          <div className="mt-4">
            <a href="/isletme" className="text-xs font-semibold text-primary hover:underline">
              {t("İşletme Profilini Tamamla →", "Complete Business Profile →")}
            </a>
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && data && (data.summary || (data.recommendations || []).length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left column: summary + recommendations */}
          <div className="lg:col-span-2 space-y-5">
            {data.summary && (
              <div className="rounded-lg border border-primary/30 bg-primary/[0.03] acas-beam p-6" data-testid="ai-insights-summary">
                <div className="acas-eyebrow text-primary/80 flex items-center gap-1.5 mb-3">
                  <Lightbulb size={12} weight="fill" />
                  {t("Pazar Durumu", "Market Situation")}
                </div>
                <p className="text-base leading-relaxed">{data.summary}</p>
              </div>
            )}

            {(data.recommendations || []).length > 0 && (
              <div className="rounded-lg border border-border/60 bg-card p-6" data-testid="ai-insights-recommendations">
                <div className="acas-eyebrow mb-3 flex items-center gap-1.5">
                  <Compass size={12} weight="fill" className="text-primary" />
                  {t("Bu Hafta Yapmanız Gerekenler", "Your Actions This Week")}
                </div>
                <ol className="space-y-3">
                  {data.recommendations.map((r, i) => {
                    const text = typeof r === "string" ? r : (r.text || r.action || JSON.stringify(r));
                    return (
                      <li key={i} className="flex gap-3 text-sm" data-testid={`ai-rec-${i}`}>
                        <div className="w-7 h-7 rounded-full bg-primary/10 text-primary grid place-items-center text-xs font-bold flex-shrink-0">
                          {i + 1}
                        </div>
                        <span className="leading-relaxed">{text}</span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {(data.highlights || []).length > 0 && (
              <div className="rounded-lg border border-border/60 bg-card p-6" data-testid="ai-insights-highlights">
                <div className="acas-eyebrow mb-3 flex items-center gap-1.5">
                  <Sparkle size={12} weight="fill" className="text-primary" />
                  {t("Sizin İçin Önemli Sinyaller", "Signals That Matter to You")}
                </div>
                <ul className="space-y-3">
                  {data.highlights.map((h, i) => (
                    <li key={i} className="text-sm border-l-2 border-primary/40 pl-3" data-testid={`ai-highlight-${i}`}>
                      <div className="font-semibold">{h.title || (typeof h === "string" ? h : "")}</div>
                      {h.impact && (
                        <div className="text-xs text-muted-foreground italic mt-1">{h.impact}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right column: sources */}
          <aside className="space-y-5">
            <div className="rounded-lg border border-border/60 bg-card p-5" data-testid="ai-insights-sources">
              <div className="acas-eyebrow mb-3 flex items-center gap-1.5">
                <Newspaper size={12} weight="fill" />
                {t("Kaynaklar", "Sources")}
              </div>
              {(data.sources || []).length === 0 && (
                <div className="text-xs text-muted-foreground">
                  {t("Bu döngüde harici haber kaynağı çekilemedi.", "No external news source could be fetched this cycle.")}
                </div>
              )}
              <ul className="space-y-2">
                {(data.sources || []).slice(0, 12).map((s, i) => (
                  <li key={i}>
                    <a
                      href={s.link}
                      target="_blank"
                      rel="noreferrer noopener"
                      data-testid={`ai-source-${i}`}
                      className="block rounded-md border border-transparent hover:border-border/60 hover:bg-primary/[0.03] p-2 transition"
                    >
                      <div className="text-xs font-semibold line-clamp-2 leading-snug">
                        {s.title}
                      </div>
                      <div className="mt-1 text-[10px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
                        {s.source && <span>{s.source}</span>}
                        {s.industry && <><span>·</span><span className="italic">{s.industry}</span></>}
                        <ArrowUpRight size={9} className="inline" />
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-dashed border-border/60 p-4 text-[11px] text-muted-foreground">
              {data.generated_at && (
                <div>
                  {t("Son üretim:", "Generated:")} {new Date(data.generated_at).toLocaleString(locale === "en" ? "en-US" : "tr-TR")}
                </div>
              )}
              {(data.industries_used || []).length > 0 && (
                <div className="mt-1">
                  {t("Sektörler:", "Industries:")} {(data.industries_used || []).join(", ")}
                </div>
              )}
              {data.refresh_blocked && (
                <div className="mt-2 flex items-start gap-1 text-amber-600 dark:text-amber-400">
                  <Warning size={11} weight="fill" className="mt-0.5" />
                  <span>{t("Manuel yenileme sınırlandı — önbelleği görüyorsunuz.", "Manual refresh throttled — showing cached copy.")}</span>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
